import { useState, useCallback } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, appId } from '../firebase';

// Bug reports live in a single flat collection (not under a per-user subtree) so
// they can be read in one place from the Firebase console. Rules allow create
// only for authenticated users; clients can never read/update/delete them.
//
// THEY OUTLIVE THE ACCOUNT ON PURPOSE — `deleteAccount` does not touch them. A
// bug is worth keeping after the reporter leaves; deleting the report would
// delete the reason to fix it.
//
// That is only lawful while a report holds nothing to erase. So it carries a
// `userId` and a `plan` and nothing else about the person (owner decision,
// 2026-09-06): the uid tells you whether two reports are the same person and
// resolves to nobody once the account is deleted, and the plan is the one fact
// that actually changes what the reporter can see — a Pro-only gate behaving
// oddly reads completely differently from a Free user hitting it as designed.
//
// It used to store `email` as well, which quietly made every bug report a
// permanent record of someone who may since have asked to be erased.
//
// DO NOT ADD IDENTIFYING FIELDS HERE. Anything personal added to this payload
// has to be erased with the account, which means changing `deleteAccount` in the
// same commit — or the app's deletion promise silently stops being true.
// `src/hooks/useBugReport.test.js` fails if an email-shaped field reappears.
export const useBugReport = (userId, plan, isDemoMode, currentPage) => {
    const [submitting, setSubmitting] = useState(false);

    const submitReport = useCallback(async (message) => {
        const trimmed = (message || '').trim();
        if (!trimmed) return { ok: false, reason: 'empty' };
        if (isDemoMode || !userId) return { ok: false, reason: 'demo' };

        setSubmitting(true);
        try {
            await addDoc(collection(db, `artifacts/${appId}/bugReports`), {
                message: trimmed.slice(0, 2000),
                page: currentPage || null,
                userId,
                // 'pro' | 'free' — what the reporter could actually see.
                plan: plan === 'pro' || plan === true ? 'pro' : 'free',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                locale: typeof navigator !== 'undefined' ? navigator.language : null,
                appVersion: process.env.REACT_APP_VERSION || null,
                status: 'new',
                createdAt: Timestamp.now(),
            });
            return { ok: true };
        } catch (error) {
            console.error('Error submitting bug report:', error);
            return { ok: false, reason: 'error' };
        } finally {
            setSubmitting(false);
        }
    }, [userId, plan, isDemoMode, currentPage]);

    return { submitReport, submitting };
};
